class VideoTranscript {
  constructor(element) {
    this.element = element.matches('.video-transcript') ? element : element.querySelector('.video-transcript');
    this.isReady = false;

    const transcriptId = this.element?.dataset.transcriptId || this.element?.id;
    if (!this.element || !transcriptId) return;

    this.transcriptId = transcriptId;

    this.trigger = document.querySelector(`[data-transcript-trigger="${transcriptId}"]`);
    if (!this.trigger) return;

    this.controlsId = this.element.dataset.controlsId || null;

    this.init();
    this.isReady = true;
  }

  init() {
    if (!this.element.id) {
      this.element.id = this.transcriptId || `transcript-${Math.random().toString(36).substring(2, 11)}`;
    }

    this.element.setAttribute('tabindex', '0');
    this.element.setAttribute('role', 'dialog');
    this.element.setAttribute('aria-modal', 'true');
    this.element.setAttribute(
      'aria-label',
      this.getLocalizedMessage('transcript_content') || 'Video transcript content'
    );
    this.element.setAttribute('aria-hidden', 'true');
    this.element.removeAttribute('open');
    this.element.setAttribute('data-transcript-id', this.element.id);

    this.trigger.setAttribute('aria-controls', this.element.id);
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.trigger.setAttribute('aria-expanded', 'false');

    this.liveRegion = Object.assign(document.createElement('div'), {
      className: 'visually-hidden',
      innerHTML: '',
      setAttribute: (attr, val) => (this.liveRegion[attr] = val),
    });
    this.liveRegion.setAttribute('aria-live', 'polite');
    this.liveRegion.setAttribute('aria-atomic', 'true');
    document.body.appendChild(this.liveRegion);

    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) {
        event.preventDefault();
        this.close();
      }
    });
    this.element.addEventListener('keydown', (event) => this.handleTranscriptKeydown(event));
  }

  isOpen() {
    return this.element.hasAttribute('open');
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }

  open() {
    if (this.isOpen()) return;
    this.pauseVideo();
    this.element.setAttribute('open', '');
    this.element.setAttribute('aria-hidden', 'false');
    this.trigger.setAttribute('aria-expanded', 'true');

    setTimeout(() => this.element.focus(), 100);
    this.announce(this.getLocalizedMessage('transcript_opened'));
  }

  close({ focusTrigger = true } = {}) {
    if (!this.isOpen()) return;
    this.element.removeAttribute('open');
    this.element.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');

    if (focusTrigger) {
      this.trigger.focus();
    }

    this.announce(this.getLocalizedMessage('transcript_closed'));
  }

  findAndPauseVideos() {
    const container = this.element.closest(
      '.article-template__featured_video, .tabbed-block__media, .aem-video-wrapper, .video-section__media, .video-section, .deferred-media, .article-template'
    );
    if (!container) {
      return;
    }

    this.pauseAllVideos(container);
  }

  pauseAllVideos(container) {
    const videos = container.querySelectorAll('video');
    videos.forEach((video) => {
      if (!video.paused) {
        video.pause();
      }
    });

    const hlsPlayers = container.querySelectorAll('hls-video-player');
    hlsPlayers.forEach((hlsPlayer) => {
      const video = hlsPlayer.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }
    });

    const deferredMediaElements = container.querySelectorAll('deferred-media');
    deferredMediaElements.forEach((deferredMedia) => {
      const video = deferredMedia.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }
    });
  }

  pauseVideo() {
    const videoContainer = this.element.closest(
      '.article-template__featured_video, .tabbed-block__media, .aem-video-wrapper, .video-section__media, .video-section'
    );
    if (!videoContainer) return;

    const video = videoContainer.querySelector('video');
    if (video && !video.paused) {
      video.pause();
    }
  }

  handleTranscriptKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  getLocalizedMessage(key) {
    const translations = window.theme?.strings || {};
    return translations[key];
  }

  announce(message) {
    if (!message) {
      return;
    }

    this.liveRegion.textContent = '';

    // Clear any pending cleanup timeouts
    if (this.announceTimeout) {
      clearTimeout(this.announceTimeout);
    }

    // Set the new message with a slight delay to ensure screen readers pick it up
    setTimeout(() => {
      this.liveRegion.textContent = message;

      // Calculate cleanup delay based on message length (rough estimate: 150-200ms per word)
      const words = message.split(' ').length;
      const baseDelay = Math.max(5000, words * 200);

      this.announceTimeout = setTimeout(() => {
        this.liveRegion.textContent = '';
        this.announceTimeout = null;
      }, baseDelay);
    }, 100);
  }
}

function initTranscripts() {
  document.querySelectorAll('.video-transcript:not([data-initialized])').forEach((el) => {
    const instance = new VideoTranscript(el);
    if (instance?.isReady) {
      el.setAttribute('data-initialized', 'true');
      el.transcriptInstance = instance;
    }
  });
}

if (!window.VideoTranscriptModule) {
  window.VideoTranscriptModule = true;

  window.VideoTranscript = window.VideoTranscript || VideoTranscript;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTranscripts);
  } else {
    initTranscripts();
  }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver((mutations) => {
      if (
        mutations.some((m) =>
          Array.from(m.addedNodes).some(
            (n) => n.nodeType === 1 && (n.matches?.('.video-transcript') || n.querySelector?.('.video-transcript'))
          )
        )
      ) {
        initTranscripts();
      }
    }).observe(document.body, { childList: true, subtree: true });
  }
}
